import React, {Fragment, useState} from 'react';

import b_ from 'b_';
import Select, {components, OptionProps, IndicatorProps} from 'react-select';

import {Button, TooltipStateful} from 'client/common/components/lego';

import {useAgencyClients, useSyncAgencyClients} from 'client/common/hooks/agency';

import I18N from 'assets/i18n/brandlift-project';

import {ClientSelectProps, Option} from './types';
import GiftIcon from './images/gift.svg';
import GiftSelectedIcon from './images/gift-selected.svg';

import './client-select.css';

const i18n = I18N.common.clientSelect;
const b = b_.with('client-select');

function ClearIndicatorComponent(props: IndicatorProps<any, any>) {
    const {ClearIndicator, CrossIcon} = components;
    const {selectProps: {value}} = props;

    return (
        <ClearIndicator {...props}>
            <Fragment>
                {value?.remainingPackageCount > 0 && <img src={GiftIcon} />}
                <CrossIcon />
            </Fragment>
        </ClearIndicator>
    );
}

function OptionComponent(props: OptionProps<any, any>) {
    const {label, data: {remainingPackageCount}, isSelected} = props;

    return (
        <components.Option {...props}>
            <div className={b('option-label')}>{label}</div>
            {remainingPackageCount > 0 && (
                <Fragment>
                    <img src={isSelected ? GiftSelectedIcon : GiftIcon} />
                    <div className={b('gift-count')}>
                        <span>{remainingPackageCount}</span>
                    </div>
                </Fragment>
            )}
        </components.Option>
    );
}

export default function ClientSelect(props: ClientSelectProps) {
    const {className = '', onChange, selectedId, showRemainPackages} = props;

    const [isSyncDisabled, setIsSyncDisabled] = useState(false);

    const clients = useAgencyClients();
    const syncAgencyClients = useSyncAgencyClients();

    const options = clients?.data?.map(({id, name, packagesLeft}) => ({
        value: id, 
        label: name, 
        remainingPackageCount: packagesLeft || 0
    }));
    const selectedValue = options?.find(({value}) => value === selectedId);

    const handleSelectChange = (option: Option | undefined) => {
        onChange(option?.value);
    };

    const handleSyncClick = async () => {
        setIsSyncDisabled(true);

        await syncAgencyClients();

        setIsSyncDisabled(false);
    };

    const cmps = showRemainPackages ? {
        Option: OptionComponent, 
        ClearIndicator: ClearIndicatorComponent
    } : {};

    return (
        <div className={`${b()} ${className}`}>
            <Select
                components={cmps}
                className={b('select')}
                classNamePrefix='react-select'
                blurInputOnSelect
                isClearable
                isLoading={!clients?.data}
                isSearchable
                onChange={handleSelectChange}
                options={options}
                placeholder={i18n.placeholder}
                value={selectedValue}
            />
            <TooltipStateful
                content={i18n.syncTooltip}
                direction='bottom-end'
                mainOffset={8}
                size='s'
                view='default'
                zIndex={1001}
            >
                <Button
                    className={b('sync-button')}
                    disabled={isSyncDisabled}
                    onClick={handleSyncClick}
                    size='m'
                    view='default'
                >
                    {i18n.sync}
                </Button>
            </TooltipStateful>
        </div>
    );
}
